import { ChallengeService } from '@duck/api-definitions'
import { DataWorkspace } from '@/components/DataWorkspace'
import { useSetAtom, useAtomValue } from 'jotai'
import { WorkspaceHeaderPane } from './WorkspaceHeaderPane'
import { MobileWorkspace } from './MobileWorkspace'
import { currentSolutionAtom } from '@/store/saveWorkAtoms'
import { ResizablePanelGroup, ResizablePanel, useSidebar } from '@duck/ui'
import { useIsMobile } from '@duck/ui'
import { useRef, useEffect, useMemo } from 'react'
import { persistPanesLayout } from '../DataWorkspace/utils'
import { DocumentsWorkspace } from '../DocumentsWorkspace'
import { debounce } from 'lodash'
import { WorkspaceStatusPane } from './WorkspaceStatusPane'
import { workspaceLayoutAtom } from '@/store/workspaceAtoms'
import { WorkspaceResizeHandle } from './WorkspaceResizeHandle'

interface WorkspaceProps {
  challenge: ChallengeService.ChallengeInfo['challenge'];
  task: ChallengeService.ChallengeInfo['task'];
  charts: ChallengeService.WorkState['charts'];
  challengeId: string;
  workTime: number;
  handleSolutionChange: (solution: string) => void;
  handleChartAdded: (solution: string, chart: ChallengeService.Chart) => void;
  handleChartRemoved: (solution: string, remainingChartIds: string[]) => void;
}

export const Workspace = ({
  challenge,
  task,
  charts,
  challengeId,
  workTime,
  handleSolutionChange,
  handleChartAdded,
  handleChartRemoved,
}: WorkspaceProps) => {
  const isMobile = useIsMobile()
  const { open } = useSidebar()
  const setCurrentSolution = useSetAtom(currentSolutionAtom)
  const workspaceLayout = useAtomValue(workspaceLayoutAtom)
  const dataEnvRef = useRef<HTMLDivElement>(null)
  const { top: topHalfSize, bottom: bottomHalfSize } = useMemo(
    () => persistPanesLayout.get('screen'),
    []
  )

  useEffect(() => {
    const solutionFromChallenge = challenge.workState?.solution || ''
    setCurrentSolution(solutionFromChallenge)
  }, [challenge, setCurrentSolution])

  const handleLayout = useMemo(
    () =>
      debounce((sizes) => {
        persistPanesLayout.save('screen', {
          top: sizes[0],
          bottom: sizes[1],
        })
      }, 100),
    []
  )

  if (isMobile) {
    return (
      <MobileWorkspace
        challenge={challenge}
        task={task}
        charts={charts}
        challengeId={challengeId}
        workTime={workTime}
        handleSolutionChange={handleSolutionChange}
        handleChartAdded={handleChartAdded}
        handleChartRemoved={handleChartRemoved}
      />
    )
  }
  return (

    <div
      className="flex flex-col flex-1 relative overflow-hidden h-screen"
      data-tour-id="challenge-env"
    >
      <WorkspaceHeaderPane challenge={challenge} task={task} workTime={workTime} />

      <ResizablePanelGroup
        direction={workspaceLayout === 'split-side' ? 'horizontal' : 'vertical'}
        className={`flex-1 border border-border border-l-0 ${open ? 'max-w-[calc(100dvw-255px)]' : 'max-w-[calc(100dvw-63px)]'}`}
        onLayout={handleLayout}
      >
        <ResizablePanel defaultSize={topHalfSize} minSize={15} maxSize={85}>
          <DocumentsWorkspace
            task={task}
            charts={charts}
            initialSolution={challenge.workState?.solution || ''}
            handleSolutionChange={handleSolutionChange}
            handleChartAdded={handleChartAdded}
            handleChartRemoved={handleChartRemoved}
          />
        </ResizablePanel>

        <WorkspaceResizeHandle enablingPanes={['top', 'bottom']} repaintOnPointerUp={workspaceLayout === 'split-side'} />

        <ResizablePanel
          defaultSize={bottomHalfSize}
          minSize={15}
          maxSize={85}
        >
          <div
            className="overflow-hidden h-full"
            ref={dataEnvRef}
          >
            <div className="h-full flex flex-col max-w-[100dvw]">
              {challengeId && (
                <DataWorkspace
                  challengeId={challengeId}
                  tableQueries={task.tableQueries}
                />
              )}
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>

      <WorkspaceStatusPane challenge={challenge} hasCharts={Object.keys(charts || {}).length > 0} />
    </div>
  )
}
